import React, { useState } from 'react'
import * as yup from 'yup'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import { CircularProgress, IconButton } from '@mui/material'
import EditIcon from '../../../../../assets/icons/Edit/Edit.svg'
import { requiredStringMinThree } from '../../../../../utils/validation'
import { topicApiSlice } from '../../../../../features/topic/topicSlice'
import { TopicState } from '../../../../../features/types'
import AddTopicDialogContent from './AddTopicDialogContent'

interface Props {
    subjectId: number
    editTopic?: TopicState
    editMode?: boolean
}

const AddTopicDialog = ({ editTopic, editMode, subjectId }: Props) => {
    const { formatMessage } = useIntl()
    const [open, setOpen] = useState(false)

    const [createTopic, { isLoading }] = topicApiSlice.useCreateTopicMutation()

    const [updateTopic, { isLoading: updateLoading }] =
        topicApiSlice.useUpdateTopicMutation()

    const topic: TopicState = {
        id: editTopic?.id || null,
        titleKz: editTopic?.titleKz || '',
        titleRu: editTopic?.titleRu || '',
        descriptionKz: editTopic?.descriptionKz || '',
        descriptionRu: editTopic?.descriptionRu || '',
        subjectId: subjectId,
    }

    const validationSchema = yup.object().shape({
        titleKz: requiredStringMinThree({ formatMessage }),
        titleRu: requiredStringMinThree({ formatMessage }),
        descriptionKz: requiredStringMinThree({ formatMessage }),
        descriptionRu: requiredStringMinThree({ formatMessage }),
    })

    const formik = useFormik({
        initialValues: topic,
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,

        onSubmit: async () => {
            if (editTopic) {
                handleUpdateTopic()
            } else {
                handleCreateTopic()
            }
        },
    })

    const handleCreateTopic = async () => {
        try {
            await createTopic(formik.values).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            handleClose()
        } catch (error) {}
    }

    const handleUpdateTopic = async () => {
        try {
            await updateTopic(formik.values).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            handleClose()
        } catch (error) {}
    }

    const handleClickOpen = () => {
        setOpen(true)
        formik.setValues(topic)
    }

    const handleClose = () => {
        setOpen(false)
    }

    return (
        <div>
            {editMode ? (
                <IconButton
                    sx={{ background: '#f3f4f5' }}
                    onClick={handleClickOpen}
                >
                    <img
                        src={EditIcon}
                        alt="edit"
                    />
                </IconButton>
            ) : (
                <Button
                    variant={'contained'}
                    onClick={handleClickOpen}
                >
                    {formatMessage({ id: 'addTopic' })}
                </Button>
            )}

            <Dialog
                maxWidth={'xl'}
                fullWidth
                open={open}
                onClose={handleClose}
            >
                <DialogTitle id="responsive-dialog-title">
                    {formatMessage({ id: 'addTopic' })}
                </DialogTitle>

                <AddTopicDialogContent formik={formik} />

                <DialogActions sx={{ m: 2 }}>
                    <Button
                        onClick={handleClose}
                        variant={'outlined'}
                        sx={{ mr: 1 }}
                        disabled={isLoading || updateLoading}
                    >
                        {formatMessage({ id: 'cancel' })}
                    </Button>

                    <Button
                        variant={'contained'}
                        sx={{ width: '120px' }}
                        onClick={() => formik.handleSubmit()}
                        disabled={isLoading || updateLoading}
                    >
                        {isLoading || updateLoading ? (
                            <CircularProgress
                                size={20}
                                style={{ marginRight: '8px' }}
                            />
                        ) : (
                            formatMessage({ id: 'save' })
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default AddTopicDialog
